import { assertNever } from '@kofno/piper';
import { Link } from '../../../../../../Resource/Types';
import { action, observable, makeObservable, computed } from 'mobx';
import { SearchParameters } from '../../../../../../ListFilter/Types';
import {
  ExperienceResource,
  ExperiencesResource,
  SelectingPrograms,
  selectProgram,
  selectScheduledPrograms,
} from './Types';
import {
  hiding,
  LoadError,
  loading,
  loadingError,
  showing,
  State,
  waiting,
  waitingWithLink,
} from './Types';
import { Maybe, just, nothing } from 'maybeasy';
import { ProductLicenseResource, ScheduledProgramInstanceOption } from '../../../../Types';

const isAlreadySelectedId = (
  state: SelectingPrograms,
  scheduledOption: ScheduledProgramInstanceOption,
): boolean => state.selectedIds.includes(scheduledOption.id);

class Store {
  @observable
  state: State = waiting();
  selectedProductLicense: Maybe<ProductLicenseResource>;

  constructor(selectedProductLicense: Maybe<ProductLicenseResource>) {
    makeObservable(this);
    this.selectedProductLicense = selectedProductLicense;
  }

  @action
  waitingWithLink = (link: Link): void => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = waitingWithLink(link);
        break;
      case 'waiting-with-link':
      case 'hiding':
      case 'loading':
      case 'loading-error':
      case 'showing':
      case 'selecting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  openModal = (): void => {
    switch (this.state.kind) {
      case 'waiting-with-link':
        this.state = loading(this.state);
        break;
      case 'hiding':
        this.state = showing(this.state.experiences, this.state);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'showing':
      case 'selecting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingError = (error: LoadError): void => {
    switch (this.state.kind) {
      case 'loading':
        this.state = loadingError(this.state.link, error);
        break;
      case 'waiting':
      case 'waiting-with-link':
      case 'loading-error':
      case 'showing':
      case 'hiding':
      case 'selecting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loaded = (experiences: ExperiencesResource): void => {
    switch (this.state.kind) {
      case 'loading':
      case 'selecting':
        this.state = showing(experiences, this.state);
        break;
      case 'waiting':
      case 'waiting-with-link':
      case 'loading-error':
      case 'showing':
      case 'hiding':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  closeModal = (): void => {
    switch (this.state.kind) {
      case 'showing':
      case 'selecting':
        this.state = hiding(this.state.experiences, this.state);
        break;
      case 'loading':
      case 'loading-error':
        this.state = waitingWithLink(this.state.link);
        break;
      case 'waiting':
      case 'waiting-with-link':
      case 'hiding':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  selectScheduledPrograms = (
    experienceResource: ExperienceResource,
    selectedIds: Array<number> = [],
  ) => {
    switch (this.state.kind) {
      case 'showing':
        this.state = selectScheduledPrograms(this.state, experienceResource, selectedIds);
        break;
      case 'loading':
      case 'loading-error':
      case 'waiting':
      case 'waiting-with-link':
      case 'hiding':
      case 'selecting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  selectProgram = (scheduledOption: ScheduledProgramInstanceOption) => {
    switch (this.state.kind) {
      case 'selecting':
        const selectedIds = isAlreadySelectedId(this.state, scheduledOption)
          ? this.state.selectedIds.filter((id) => id !== scheduledOption.id)
          : [...this.state.selectedIds, scheduledOption.id];
        this.state = selectProgram(selectedIds, this.state);
        break;
      case 'showing':
      case 'loading':
      case 'loading-error':
      case 'waiting':
      case 'waiting-with-link':
      case 'hiding':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get sortParameters(): SearchParameters {
    switch (this.state.kind) {
      case 'showing':
      case 'loading':
      case 'hiding':
      case 'waiting-with-link':
      case 'selecting':
        return this.state.sortParameters;
      case 'loading-error':
      case 'waiting':
        return [];
    }
  }

  @computed
  get experienceResource(): Maybe<ExperienceResource> {
    switch (this.state.kind) {
      case 'selecting':
        return just(this.state.experienceResource);
      case 'showing':
      case 'loading':
      case 'hiding':
      case 'waiting-with-link':
      case 'loading-error':
      case 'waiting':
        return nothing();
    }
  }

  @computed
  get selectedIds(): number[] {
    switch (this.state.kind) {
      case 'selecting':
        return this.state.selectedIds;
      case 'showing':
      case 'loading':
      case 'loading-error':
      case 'waiting':
      case 'waiting-with-link':
      case 'hiding':
        return [];
      default:
        assertNever(this.state);
    }
  }
}

export default Store;
